import { useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FlaskConical, Hospital } from "lucide-react";

/**
 * Two OPD-side action buttons for a doctor's current patient:
 *   1. Lab Test — disabled placeholder for a future workflow.
 *   2. Admit to IPD — opens a short modal to capture the diagnosis, then
 *      posts an admission REQUEST to the backend (no bed allocated yet).
 *      Receptionist confirms & allocates a bed via the IPD dashboard.
 */
export default function OpdToIpdActions({ patient }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    reason_for_admission: "", chief_complaint: "",
    past_medical_history: "", allergies: "", current_medications: "",
    temperature: "", blood_pressure: "", heart_rate: "", respiratory_rate: "",
  });

  const submit = async () => {
    if (!patient?.mobile) { toast.error("No patient in cabin"); return; }
    if (f.reason_for_admission.trim().length < 2) {
      toast.error("Reason for admission is required"); return;
    }
    setSaving(true);
    try {
      await api.post("/ipd/admissions/request", {
        mobile: patient.mobile,
        name: patient.name,
        age: patient.age,
        gender: patient.gender,
        reason_for_admission: f.reason_for_admission,
        chief_complaint: f.chief_complaint,
        past_medical_history: f.past_medical_history,
        allergies: f.allergies,
        current_medications: f.current_medications
          ? f.current_medications.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 50)
          : [],
        vitals_on_admission: {
          temperature: f.temperature ? Number(f.temperature) : null,
          blood_pressure: f.blood_pressure || null,
          heart_rate: f.heart_rate ? Number(f.heart_rate) : null,
          respiratory_rate: f.respiratory_rate ? Number(f.respiratory_rate) : null,
        },
      });
      toast.success("Admission request sent to receptionist");
      setOpen(false);
      setF({ reason_for_admission: "", chief_complaint: "", past_medical_history: "",
             allergies: "", current_medications: "",
             temperature: "", blood_pressure: "", heart_rate: "", respiratory_rate: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send request");
    } finally { setSaving(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" disabled
              className="gap-1.5 opacity-60 cursor-not-allowed"
              data-testid="opd-lab-test-btn">
        <FlaskConical size={14} />
        Lab Test <span className="text-[10px] font-normal">· Coming Soon</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button size="sm" className="gap-1.5 bg-blue-500 hover:bg-blue-600 text-white"
                  data-testid="opd-admit-ipd-btn">
            <Hospital size={14} /> Admit to IPD
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto"
                       data-testid="opd-admit-ipd-modal">
          <DialogHeader><DialogTitle>Send Admission Request</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Patient: <strong>{patient?.name}</strong> · age {patient?.age} · {patient?.mobile}
          </p>
          <div className="grid gap-3">
            <div><Label>Reason for admission *</Label>
              <Textarea value={f.reason_for_admission} maxLength={500}
                        onChange={(e) => setF({ ...f, reason_for_admission: e.target.value })}
                        placeholder="e.g. Severe abdominal pain, suspected appendicitis"
                        data-testid="opd-admit-reason" /></div>
            <div><Label>Chief complaint</Label>
              <Textarea value={f.chief_complaint} maxLength={1000}
                        onChange={(e) => setF({ ...f, chief_complaint: e.target.value })} /></div>
            <div><Label>Past medical history</Label>
              <Textarea value={f.past_medical_history} maxLength={2000}
                        onChange={(e) => setF({ ...f, past_medical_history: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Allergies</Label>
                <Input value={f.allergies} maxLength={500}
                       onChange={(e) => setF({ ...f, allergies: e.target.value })} /></div>
              <div><Label>Meds (comma-sep)</Label>
                <Input value={f.current_medications}
                       onChange={(e) => setF({ ...f, current_medications: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1">
              <div><Label>Temp °C</Label>
                <Input type="number" step="0.1" value={f.temperature}
                       onChange={(e) => setF({ ...f, temperature: e.target.value })} /></div>
              <div><Label>BP</Label>
                <Input value={f.blood_pressure} placeholder="120/80"
                       onChange={(e) => setF({ ...f, blood_pressure: e.target.value })} /></div>
              <div><Label>HR</Label>
                <Input type="number" value={f.heart_rate}
                       onChange={(e) => setF({ ...f, heart_rate: e.target.value })} /></div>
              <div><Label>RR</Label>
                <Input type="number" value={f.respiratory_rate}
                       onChange={(e) => setF({ ...f, respiratory_rate: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-500 hover:bg-blue-600 text-white"
                    data-testid="opd-admit-submit">
              {saving ? "Sending…" : "Send to Receptionist"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
